import React, { useEffect, useId, useRef, useState } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STAR_PATH =
  "M11.525 2.295a.53.53 0 0 1 .95 0l2.31 4.679a2.123 2.123 0 0 0 1.595 1.16l5.166.756a.53.53 0 0 1 .294.904l-3.736 3.638a2.123 2.123 0 0 0-.611 1.878l.882 5.14a.53.53 0 0 1-.771.56l-4.618-2.428a2.122 2.122 0 0 0-1.973 0L6.396 21.01a.53.53 0 0 1-.77-.56l.881-5.139a2.122 2.122 0 0 0-.611-1.879L2.16 9.795a.53.53 0 0 1 .294-.906l5.165-.755a2.122 2.122 0 0 0 1.597-1.16z";

export function TestimonialCard({ quote, author, designation, rating, url }) {
  const filledStars = rating ? Math.round(rating) : 0;
  const starGradientId = `star-grad-${useId().replace(/:/g, "")}`;
  const quoteRef = useRef(null);
  const [isTruncated, setIsTruncated] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    const el = quoteRef.current;
    if (el && el.scrollHeight > el.clientHeight) {
      setIsTruncated(true);
    }
  }, [quote]);

  useEffect(() => {
    if (!isModalOpen) return undefined;
    const handleKeyDown = (e) => {
      if (e.key === "Escape") setIsModalOpen(false);
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isModalOpen]);

  return (
    <>
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        padding: "26px 24px",
        borderRadius: 16,
        background: "linear-gradient(145deg, #1e3331 0%, #172a28 100%)",
        border: "1px solid rgba(91,148,144,0.35)",
        boxShadow: "0 8px 28px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.07)",
      }}
    >
      {rating > 0 && (
        <div style={{ display: "flex", gap: 3, marginBottom: 14 }}>
          <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden="true">
            <defs>
              <linearGradient id={starGradientId} x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#C2E8E1" />
                <stop offset="50%" stopColor="#5B9490" />
                <stop offset="100%" stopColor="#2D5C5A" />
              </linearGradient>
            </defs>
          </svg>
          {Array.from({ length: 5 }, (_, i) => (
            <svg
              key={i}
              data-testid="star"
              data-filled={i < filledStars ? "true" : "false"}
              width={15}
              height={15}
              viewBox="0 0 24 24"
              fill={i < filledStars ? `url(#${starGradientId})` : "rgba(166,210,206,0.35)"}
              style={
                i < filledStars
                  ? { filter: "drop-shadow(0 1px 1px rgba(0,0,0,0.45)) drop-shadow(0 0 3px rgba(91,148,144,0.45))" }
                  : undefined
              }
              aria-hidden="true"
            >
              <path d={STAR_PATH} />
            </svg>
          ))}
        </div>
      )}

      <div
        ref={quoteRef}
        data-testid="quote-text"
        style={{
          fontFamily: "Montserrat, sans-serif",
          fontWeight: 300,
          fontSize: 14,
          lineHeight: 1.62,
          color: "#dde8e7",
          height: "calc(1.62em * 5)",
          display: "-webkit-box",
          WebkitLineClamp: 5,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {quote}
      </div>

      <div data-testid="read-full-slot" style={{ height: 24, marginTop: 8 }}>
        {isTruncated && (
          <button
            type="button"
            onClick={() => setIsModalOpen(true)}
            style={{
              padding: 0,
              background: "none",
              border: "none",
              fontFamily: "Montserrat, sans-serif",
              fontWeight: 500,
              fontSize: 12,
              color: "#7BB5B1",
              textDecoration: "underline",
              cursor: "pointer",
            }}
          >
            Read Full
          </button>
        )}
      </div>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginTop: 20,
          paddingTop: 16,
          borderTop: "1px solid rgba(255,255,255,0.10)",
        }}
      >
        <div>
          {author && (
            <div
              data-testid="testimonial-author"
              style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 600, fontSize: 13, color: "#F5F7F7" }}
            >
              {author}
            </div>
          )}
          {designation && (
            <div
              data-testid="testimonial-designation"
              style={{
                fontFamily: "Montserrat, sans-serif",
                fontWeight: 400,
                fontSize: 11,
                color: "rgba(255,255,255,0.42)",
                marginTop: 2,
              }}
            >
              {designation}
            </div>
          )}
        </div>
        {url && (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              fontFamily: "Montserrat, sans-serif",
              fontWeight: 500,
              fontSize: 11,
              color: "#7BB5B1",
              whiteSpace: "nowrap",
            }}
          >
            Google →
          </a>
        )}
      </div>
    </div>

    {isModalOpen && (
      <div
        data-testid="review-modal-overlay"
        onClick={() => setIsModalOpen(false)}
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0,0,0,0.7)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 1000,
          padding: 24,
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            display: "flex",
            flexDirection: "column",
            padding: "32px 28px",
            borderRadius: 16,
            background: "linear-gradient(145deg, #1e3331 0%, #172a28 100%)",
            border: "1px solid rgba(91,148,144,0.35)",
            boxShadow: "0 8px 28px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.07)",
            maxWidth: 480,
            width: "90%",
            maxHeight: "80vh",
            overflowY: "auto",
          }}
        >
          <button
            type="button"
            aria-label="Close"
            onClick={() => setIsModalOpen(false)}
            style={{
              alignSelf: "flex-end",
              background: "none",
              border: "none",
              color: "#dde8e7",
              fontSize: 20,
              lineHeight: 1,
              cursor: "pointer",
              marginBottom: 8,
            }}
          >
            ×
          </button>

          {rating > 0 && (
            <div style={{ display: "flex", gap: 3, marginBottom: 14 }}>
              {Array.from({ length: 5 }, (_, i) => (
                <svg
                  key={i}
                  width={15}
                  height={15}
                  viewBox="0 0 24 24"
                  fill={i < filledStars ? `url(#${starGradientId})` : "rgba(166,210,206,0.35)"}
                  style={
                    i < filledStars
                      ? { filter: "drop-shadow(0 1px 1px rgba(0,0,0,0.45)) drop-shadow(0 0 3px rgba(91,148,144,0.45))" }
                      : undefined
                  }
                  aria-hidden="true"
                >
                  <path d={STAR_PATH} />
                </svg>
              ))}
            </div>
          )}

          <div
            data-testid="modal-quote-text"
            style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 300, fontSize: 14, lineHeight: 1.62, color: "#dde8e7" }}
          >
            {quote}
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              marginTop: 20,
              paddingTop: 16,
              borderTop: "1px solid rgba(255,255,255,0.10)",
            }}
          >
            <div>
              {author && (
                <div style={{ fontFamily: "Montserrat, sans-serif", fontWeight: 600, fontSize: 13, color: "#F5F7F7" }}>
                  {author}
                </div>
              )}
              {designation && (
                <div
                  style={{
                    fontFamily: "Montserrat, sans-serif",
                    fontWeight: 400,
                    fontSize: 11,
                    color: "rgba(255,255,255,0.42)",
                    marginTop: 2,
                  }}
                >
                  {designation}
                </div>
              )}
            </div>
            {url && (
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  fontFamily: "Montserrat, sans-serif",
                  fontWeight: 500,
                  fontSize: 11,
                  color: "#7BB5B1",
                  whiteSpace: "nowrap",
                }}
              >
                Google →
              </a>
            )}
          </div>
        </div>
      </div>
    )}
    </>
  );
}

function Testimonials() {
  const [reviews, setReviews] = useState([]);

  useEffect(() => {
    axios
      .get(`${API}/reviews`)
      .then((res) => {
        setReviews(res.data?.data || []);
      })
      .catch(() => {});
  }, []);

  if (reviews.length === 0) return null;

  return (
    <section id="reviews" style={{ position: "relative", width: "100%", background: "#070808" }}>
      <style>{`
        @media (max-width: 767px) {
          .testimonials-inner { padding: 56px 20px 48px !important; }
          .testimonials-row { gap: 14px !important; }
          .testimonial-slide { flex: 0 0 calc((100% - 14px) / 1.5) !important; }
        }
      `}</style>
      <div className="testimonials-inner" style={{ padding: "72px 64px 64px" }}>
        <div style={{ textAlign: "center", marginBottom: 48 }}>
          <h2
            style={{
              margin: 0,
              fontFamily: "Montserrat, sans-serif",
              fontWeight: 300,
              fontSize: "clamp(32px, 5vw, 48px)",
              lineHeight: 1.15,
              letterSpacing: "-0.02em",
            }}
          >
            <span style={{ color: "#FFFFFF" }}>What clients say</span>
          </h2>
        </div>

        <div
          className="testimonials-row"
          style={{
            display: "flex",
            gap: 20,
            padding: "4px 4px 20px",
            overflowX: "auto",
            overflowY: "hidden",
            scrollSnapType: "x mandatory",
          }}
        >
          {reviews.map((review) => (
            <div
              key={review.id}
              data-testid="testimonial-slide"
              className="testimonial-slide"
              style={{
                flex: "0 0 clamp(260px, calc((100% - 40px) / 2.5), 420px)",
                scrollSnapAlign: "start",
              }}
            >
              <TestimonialCard
                quote={review.quote}
                author={review.author}
                designation={review.designation}
                rating={review.rating}
                url={review.url}
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default Testimonials;
